/* eslint-disable @angular-eslint/directive-selector */
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CdkTable } from '@angular/cdk/table';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  Directive,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Pipe,
  PipeTransform,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  booleanAttribute,
  input,
  inject,
} from '@angular/core';
import { Pageable, SearchResponse } from 'api';
import { Observable, Subject, tap } from 'rxjs';
import { mergeWith } from 'rxjs/operators';

@Directive({
  selector: 'ss-ng-table-header',
  standalone: false,
})
export class SsTableHeaderDirective {}

@Directive({
  selector: 'ss-ng-table-footer',
  standalone: false,
})
export class SsTableFooterDirective {}

@Directive({
  selector: '[ssNgCellKey]',
  standalone: false,
})
export class SsNgCellKeyDirective<T> {
  templateRef = inject<TemplateRef<any>>(TemplateRef);

  @Input() ssNgCellKey?: string;

  /**
   * The data source for the table
   * This can be an array or an observable
   * This allows for type safe use of let-element
   */
  @Input() ssNgCellDataSource?:
    | T[]
    | Observable<T[]>
    | Observable<SearchResponse<T>>;

  static ngTemplateContextGuard<T>(
    dir: SsNgCellKeyDirective<T>,
    ctx: unknown
  ): ctx is { $implicit: T; index: number } {
    return true;
  }
}

@Directive({
  selector: '[ssNgHeaderKey]',
  standalone: false,
})
export class SsNgHeaderKeyDirective {
  templateRef = inject<TemplateRef<any>>(TemplateRef);

  @Input() ssNgHeaderKey?: string;
}

@Pipe({
  name: 'tableTemplate',
  standalone: false,
})
export class SsTableTemplatePipe implements PipeTransform {
  transform(
    templateId: string,
    cells:
      | QueryList<SsNgCellKeyDirective<any> | SsNgHeaderKeyDirective>
      | undefined
  ): TemplateRef<any> | undefined {
    if (!cells) {
      return undefined;
    }
    return cells?.find((c) => {
      if (c instanceof SsNgCellKeyDirective) {
        return c.ssNgCellKey === templateId;
      } else {
        return c.ssNgHeaderKey === templateId;
      }
    })?.templateRef;
  }
}

@Component({
  selector: 'ss-ng-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SsTableComponent implements OnChanges, OnInit {
  @ViewChild('table') table?: CdkTable<any>;
  /**
   * The property names of the object to be displayed in the table
   * Columns are in order of the properties in this array
   * This can be dynamically set by the parent component
   * so you can add or remove columns if needed.
   * You can use strings that are not properties of the object
   * but you must override the cell template for that column
   */
  @Input() columnsDef?: string[];
  secondRowColumnsDef = input<string[]>([]);
  /**
   * The property names of the object that should have sort enabled.
   * Columns default to not being sortable
   */
  @Input() sortableColumns?: string[];

  /**
   * The observable that provides the data to the table
   * It can take either a search SearchResponse or standard API response
   */
  @Input() getData$?: Observable<any> | null;

  _getData$?: Observable<any> | null;

  /**
   * Whether or not the table should display the pagination controls
   */
  @Input() paging?: boolean;

  /**
   * The selection mode of the table
   */
  @Input() selection?: 'single' | 'multiple' | null;

  /**
   * This is used to show the loading state when data for the table
   * is being re-fetched outside of the table component action like
   * sorting or paging
   */
  @Input() isTableReloading?: boolean | null;

  selected = new SelectionModel<any>(this.selection === 'multiple', []);

  /**
   * The pageable state of the table
   * Provide the value of the pageable state from the parent component
   */
  @Input() pageableState?: Pageable | null = {
    size: 10,
    page: 0,
  };

  @Input({ transform: booleanAttribute }) whiteBackground?: boolean = false;

  /**
   * Used to add a class to table rows given certain conditions.
   * Takes a function that returns whether the row should be formatted.
   * The entire row object is passed into the function.
   * @example (row: MyRowInterface) => row.status === 'Active' ? 'active-row' : ''
   */
  @Input() rowNgClassFn?: (row: any) => string | string[] | Set<string> | null;

  @Input() set disableRowClick(value: boolean) {
    this.disableClick = value;
    this.validateRowIsListening();
  }

  @Input() hidePaginationNumbers?: boolean = false;
  @Input() dragAndDrop?: boolean = false;

  @ContentChildren(SsNgCellKeyDirective, { descendants: true })
  cells?: QueryList<SsNgCellKeyDirective<any>>;

  @ContentChildren(SsNgHeaderKeyDirective, { descendants: true })
  headers?: QueryList<SsNgHeaderKeyDirective>;

  /**
   * Emits when the pageable state changes like clicking next page
   * This event can be mapped to a behavior subject that is switchedMap
   * to the data observable
   */
  @Output() pageableChange = new EventEmitter<Pageable>();

  /**
   * Emits when a row is clicked with the row data
   * If you are trying to use both rowClick and a clickable button in the row
   */
  @Output() rowClick = new EventEmitter<any>();

  /**
   * Emits when the selection changes with the selected rows
   * This emitter occurs both for single and multiple selection
   */
  @Output() selectionChange = new EventEmitter<any>();
  @Output() sortChanged = new EventEmitter<any>();

  private mousePosition?: { x: number; y: number };
  public rowIsListening = false;
  private disableClick = false;
  private sortEmission$ = new Subject<any>();

  ngOnInit() {
    this.validateRowIsListening();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['getData$']?.currentValue !== changes['getData$']?.previousValue
    ) {
      this._getData$ = this.getData$?.pipe(
        mergeWith(this.sortEmission$),
        // reset the table reloading state
        tap(() => (this.isTableReloading = false)),
        // clear the selection when the data changes
        tap(() => {
          if (this.selected.selected.length > 0) {
            this.selected.clear();
            this.selectionChange.emit(this.selected.selected);
          }
        }),
        // render the table rows, only needed for client side sorting
        // I think due to the reference not changing, it wasn't rerendering
        // material does this for performance
        // https://material.angular.io/components/table/overview#1-write-your-mat-table-and-provide-data
        tap(() => this.table?.renderRows())
      );
    }
    if (
      changes['selection']?.currentValue !== changes['selection']?.previousValue
    ) {
      this.selected = new SelectionModel<any>(
        this.selection === 'multiple',
        []
      );
    }
  }

  sort(columnName: string): void {
    const direction =
      this.pageableState?.sort?.[0].field === columnName &&
      this.pageableState.sort?.[0].dir === 'asc'
        ? 'desc'
        : 'asc';
    this.pageableChange.emit({
      ...this.pageableState,
      sort: [{ field: columnName, dir: direction }],
    });
  }

  isSortable(column: string): boolean {
    if (this.sortableColumns) {
      return !!this.sortableColumns?.find((c) => c === column);
    }
    return false;
  }

  pageChange(page: number): void {
    this.selected.clear();
    this.pageableChange.emit({ ...this.pageableState, page });
  }

  isAllSelected(data: any[]): boolean {
    const numSelected = this.selected.selected.length;
    const numRows = data.length;
    return numSelected == numRows;
  }

  onSelectionChange(row: any): void {
    this.selected.toggle(row);
    this.selectionChange.emit(this.selected.selected);
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows(data: any[]) {
    this.isAllSelected(data)
      ? this.selected.clear()
      : data.forEach((row) => this.selected.select(row));
    this.selectionChange.emit(this.selected.selected);
  }

  onRowMouseDown(event: Event): void {
    if (event instanceof MouseEvent) {
      this.mousePosition = { x: event.clientX, y: event.clientY };
    }
  }

  onRowClick(event: MouseEvent, row: any): void {
    if (this.isClickNotClickDrag(event) && !this.disableClick) {
      this.rowClick.emit(row);
    }
  }

  private isClickNotClickDrag(event: MouseEvent): boolean {
    const x = event.clientX;
    const y = event.clientY;

    // the wiggle room is to account for the mouse moving slightly
    // may need to be adjusted depending on user feedback
    // may be a bad idea in general since we can get feedback that click isn't working
    const wiggleRoom = 5;

    if (!this.mousePosition) return true;
    return (
      Math.abs(this.mousePosition?.x - x) +
        Math.abs(this.mousePosition?.y - y) <
      wiggleRoom
    );
  }

  get _columnsDef(): string[] {
    const cols = [...(this.columnsDef || [])];
    if (this.dragAndDrop) cols.push('dragAndDrop');
    else if (this.selection) cols.unshift('ssNgSelection');
    return cols;
  }

  conditionalClass() {
    return { myClass: true };
  }

  private validateRowIsListening() {
    this.rowIsListening =
      !this.disableClick && this.rowClick.observed ? true : false;
  }

  drop(event: CdkDragDrop<any>, data: any[]) {
    moveItemInArray(data, event.previousIndex, event.currentIndex);
    this.sortEmission$.next(data);
    this.sortChanged.emit(data);
    this.table?.renderRows();
  }
}
